import { useQueryClient, type QueryClient } from "@tanstack/react-query";
import { useEffect, useState, type ReactNode } from "react";

import { api, unwrap } from "@/shared/api/client";
import { applyRealtimeEvent, hasPendingEdits } from "@/shared/realtime/applyRealtimeEvent";
import {
  RealtimeConnection,
  socketUrlFor,
  type RealtimeStatus,
  type Ticket,
} from "@/shared/realtime/connection";
import { RealtimeStatusContext } from "@/shared/realtime/context";
import type { RealtimeEnvelope } from "@/shared/realtime/envelope";

/* Events held back while a project's task edits are in flight, at most this many each. */
const MAX_DEFERRED = 50;

async function fetchTicket(): Promise<Ticket> {
  return unwrap(api.POST("/api/v1/realtime/ticket"));
}

/**
 * Holds task events back while the project's edits are on their way to the server (a refetch
 * then could undo them on screen), and replays them once the queue is empty.
 */
function deferredEvents(queryClient: QueryClient) {
  // By project key; null holds tasks outside any project.
  const held = new Map<string | null, RealtimeEnvelope[]>();
  const apply = (envelope: RealtimeEnvelope) => {
    if (applyRealtimeEvent(queryClient, envelope) !== "deferred") return;
    const key = envelope.project_key;
    held.set(key, [...(held.get(key) ?? []), envelope].slice(-MAX_DEFERRED));
  };
  const unsubscribe = queryClient.getMutationCache().subscribe(() => {
    for (const [key, envelopes] of held) {
      if (hasPendingEdits(queryClient, key)) continue;
      held.delete(key);
      envelopes.forEach(apply);
    }
  });
  return { apply, unsubscribe };
}

/**
 * Live updates for the signed-in team member: changes other people make show up without a
 * refresh. Mount it inside the session; `userId` null (signed out) closes the connection.
 */
export function RealtimeProvider({
  userId,
  children,
}: {
  userId: string | null;
  children: ReactNode;
}) {
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<RealtimeStatus>("idle");

  useEffect(() => {
    if (userId === null) return;
    const events = deferredEvents(queryClient);
    const connection = new RealtimeConnection({
      fetchTicket,
      openSocket: (url) => new WebSocket(url),
      socketUrl: socketUrlFor(window.location),
      onEvent: events.apply,
      onResync: () => void queryClient.invalidateQueries(),
      onStatus: setStatus,
    });
    const onVisibility = () => {
      connection.setVisible(document.visibilityState === "visible");
    };
    const onOnline = () => {
      connection.reconnectNow();
    };
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("online", onOnline);
    connection.start();
    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("online", onOnline);
      connection.stop();
      events.unsubscribe();
    };
  }, [queryClient, userId]);

  return (
    <RealtimeStatusContext.Provider value={userId === null ? "idle" : status}>
      {children}
    </RealtimeStatusContext.Provider>
  );
}
